import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  AlertCircleIcon,
  CarFrontIcon,
  CheckCircle2Icon,
  GaugeIcon,
  PlusIcon,
  TruckIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ChassisCodeChip } from "@/components/app/chassis-codes";
import { EmptyState } from "@/components/app/empty-state";
import { FileImage } from "@/components/app/file-image";
import { Page, PageHeader } from "@/components/app/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/misc";
import { Switch } from "@/components/ui/switch";
import { VehicleSheet } from "@/features/vehicles/vehicle-sheet";
import { useFormat } from "@/lib/format";
import { searchText } from "@/lib/search";
import { useTRPC } from "@/lib/trpc";

export const Route = createFileRoute("/vehicles/")({
  validateSearch: z.object({ new: searchText, archived: searchText }),
  component: VehiclesPage,
});

function VehiclesPage() {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const format = useFormat();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const includeArchived = search.archived === "1";
  const { data: vehicles, isLoading } = useQuery(
    trpc.vehicles.list.queryOptions({ includeArchived }),
  );

  return (
    <Page wide>
      <PageHeader
        title={t("vehicles.title")}
        subtitle={vehicles ? t("vehicles.count", { count: vehicles.length }) : " "}
        actions={
          <>
            <label
              htmlFor="show-archived"
              className="mr-2 flex items-center gap-2 text-sm text-muted-foreground"
            >
              <Switch
                id="show-archived"
                checked={includeArchived}
                onCheckedChange={(checked) =>
                  void navigate({ search: (s) => ({ ...s, archived: checked ? "1" : undefined }) })
                }
              />
              {t("vehicles.showArchived")}
            </label>
            <Button onClick={() => void navigate({ search: (s) => ({ ...s, new: "1" }) })}>
              <PlusIcon />
              {t("vehicles.new")}
            </Button>
          </>
        }
      />
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 3 }, (_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static placeholders
            <Skeleton key={i} className="h-72 rounded-2xl" />
          ))}
        </div>
      ) : !vehicles?.length ? (
        <EmptyState
          icon={CarFrontIcon}
          title={t("vehicles.emptyTitle")}
          body={t("vehicles.emptyBody")}
          action={
            <Button onClick={() => void navigate({ search: (s) => ({ ...s, new: "1" }) })}>
              <PlusIcon />
              {t("vehicles.new")}
            </Button>
          }
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {vehicles.map((v) => (
            <Link
              key={v.id}
              to="/vehicles/$vehicleId"
              params={{ vehicleId: v.id }}
              className="group overflow-hidden rounded-2xl border border-border bg-surface shadow-xs transition hover:-translate-y-0.5 hover:shadow-md focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:outline-none"
            >
              <div className="relative">
                <FileImage
                  fileId={v.coverFileId}
                  alt=""
                  icon={CarFrontIcon}
                  full
                  className="aspect-[16/9] w-full rounded-none bg-gradient-to-br from-muted to-subtle"
                />
                {v.chassisCode || v.engineCode ? (
                  <div className="absolute top-3 left-3 flex gap-1.5">
                    {v.chassisCode ? (
                      <ChassisCodeChip code={v.chassisCode} className="px-1.5 py-0.5" />
                    ) : null}
                    {v.engineCode ? (
                      <span className="rounded-md bg-black/55 px-1.5 py-0.5 font-mono text-[11px] font-medium text-white backdrop-blur">
                        {v.engineCode}
                      </span>
                    ) : null}
                  </div>
                ) : null}
                {v.archived ? (
                  <Badge tone="neutral" className="absolute top-3 right-3">
                    {t("common.archived")}
                  </Badge>
                ) : null}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="truncate text-base font-semibold tracking-tight">{v.name}</h2>
                    <p className="truncate text-sm text-muted-foreground">
                      {[v.make, v.model, v.modelYear].filter(Boolean).join(" · ") || " "}
                    </p>
                  </div>
                  {v.mileage !== null ? (
                    <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground tabular">
                      <GaugeIcon className="size-3.5" />
                      {format.mileage(v.mileage, v.mileageUnit)}
                    </span>
                  ) : null}
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-1.5 border-t border-border pt-3 text-xs text-muted-foreground">
                  {v.openWorkOrders === 0 ? (
                    <span>
                      {v.lastServiceOn
                        ? t("vehicles.lastService", { date: format.date(v.lastServiceOn) })
                        : t("vehicles.noService")}
                    </span>
                  ) : (
                    <>
                      <span className="mr-1">
                        {t("vehicles.openWorkOrders", { count: v.openWorkOrders })}
                      </span>
                      {v.readiness.missing ? (
                        <Badge tone="missing">
                          <AlertCircleIcon />
                          {v.readiness.missing}
                        </Badge>
                      ) : null}
                      {v.readiness.waiting ? (
                        <Badge tone="transit">
                          <TruckIcon />
                          {v.readiness.waiting}
                        </Badge>
                      ) : null}
                      {v.readiness.ready ? (
                        <Badge tone="ok">
                          <CheckCircle2Icon />
                          {v.readiness.ready}
                        </Badge>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
      <VehicleSheet
        open={search.new === "1"}
        onOpenChange={(open) =>
          void navigate({ search: (s) => ({ ...s, new: open ? "1" : undefined }) })
        }
        onSaved={(id) =>
          void navigate({ to: "/vehicles/$vehicleId", params: { vehicleId: id }, search: {} })
        }
      />
    </Page>
  );
}
